import type { Invitation, TenantUser } from "@station/contracts";
import {
  Alert,
  Button,
  ConfirmDialog,
  DataTable,
  type DataTableColumn,
  Drawer,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  EmptyState,
  FormField,
  PageHeader,
  Select,
  StatusBadge,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  type Tone,
  useToast,
} from "@station/ui";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Ellipsis, MailPlus, UserPlus, Users } from "lucide-react";
import { type ReactElement, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { InviteForm } from "../../../features/invite-form.js";
import { UserRolesForm } from "../../../features/user-roles-form.js";
import { api } from "../../../lib/api.js";
import { useCursorPages } from "../../../lib/cursor-pages.js";
import { errorMessage } from "../../../lib/errors.js";
import { exportRowsToExcel } from "../../../lib/excel.js";
import { formatDateTime } from "../../../lib/format.js";
import { quickActionSearch, useDocumentTitle, useOpenFromQuickAction } from "../../../lib/hooks.js";
import { queryKeys } from "../../../lib/query.js";
import { useMe, usePermissions } from "../../../lib/session.js";
import { useStepUp } from "../../../lib/step-up.js";

export const Route = createFileRoute("/_app/ayarlar/kullanicilar")({
  validateSearch: (search) => z.object(quickActionSearch).parse(search),
  component: UsersPage,
});

const MEMBERSHIP_TONE: Record<TenantUser["membershipStatus"], Tone> = {
  ACTIVE: "success",
  SUSPENDED: "warning",
  REMOVED: "neutral",
};
const INVITATION_TONE: Record<Invitation["status"], Tone> = {
  PENDING: "info",
  ACCEPTED: "success",
  REVOKED: "neutral",
  EXPIRED: "warning",
};
const INVITATION_STATUSES = ["PENDING", "ACCEPTED", "REVOKED", "EXPIRED"] as const;

function UsersPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const permissions = usePermissions();
  useDocumentTitle(t("menu.users"));
  const [inviting, setInviting] = useState(false);
  const [tab, setTab] = useState("users");
  useOpenFromQuickAction(
    Route.useSearch().yeni === "1",
    () => setInviting(true),
    permissions.has("iam.user.invite"),
  );

  return (
    <>
      <PageHeader
        title={t("menu.users")}
        description={t("users.description")}
        breadcrumb={[{ label: t("menu.groups.settings") }, { label: t("menu.users") }]}
        actions={
          permissions.has("iam.user.invite") ? (
            <Button onClick={() => setInviting(true)}>
              <UserPlus aria-hidden="true" />
              {t("users.invite")}
            </Button>
          ) : null
        }
      />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList aria-label={t("menu.users")}>
          <TabsTrigger value="users">{t("users.tabs.users")}</TabsTrigger>
          <TabsTrigger value="invitations">{t("users.tabs.invitations")}</TabsTrigger>
        </TabsList>
        <TabsContent value="users">
          <UsersTab />
        </TabsContent>
        <TabsContent value="invitations">
          <InvitationsTab />
        </TabsContent>
      </Tabs>

      <Drawer
        open={inviting}
        onOpenChange={setInviting}
        title={t("users.invite")}
        description={t("users.inviteDescription")}
      >
        {inviting ? (
          <InviteForm
            onCancel={() => setInviting(false)}
            onInvited={(email) => {
              setInviting(false);
              setTab("invitations");
              toast({ tone: "success", title: t("users.invitationSent", { email }) });
            }}
          />
        ) : null}
      </Drawer>
    </>
  );
}

function UsersTab(): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const withStepUp = useStepUp();
  const permissions = usePermissions();
  const myId = useMe().data?.user.id;
  const [editing, setEditing] = useState<TenantUser | null>(null);
  const [confirming, setConfirming] = useState<{
    user: TenantUser;
    status: TenantUser["membershipStatus"];
  } | null>(null);

  const users = useQuery({
    queryKey: queryKeys.users,
    queryFn: async () => (await api.GET("/users")).data!.items,
  });
  const setStatus = useMutation({
    mutationFn: async (input: { userId: string; status: TenantUser["membershipStatus"] }) =>
      withStepUp(() =>
        api.PUT("/users/{userId}/membership", {
          params: { path: { userId: input.userId } },
          body: { status: input.status },
        }),
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.users });
      toast({ tone: "success", title: t("common.saved") });
    },
    onError: (error) => toast({ tone: "danger", title: errorMessage(error) }),
  });

  const canAssign = permissions.has("iam.role.assign");
  const canManage = permissions.has("iam.user.manage");

  const columns = useMemo<DataTableColumn<TenantUser>[]>(
    () => [
      {
        id: "fullName",
        header: t("auth.fullName"),
        hideable: false,
        cell: (row) => (
          <div className="grid">
            <span className="font-medium">
              {row.fullName}
              {row.userId === myId ? (
                <span className="ml-2 text-xs font-normal text-muted-foreground">({t("users.you")})</span>
              ) : null}
            </span>
            <span className="text-xs text-muted-foreground">{row.email}</span>
          </div>
        ),
        exportValue: (row) => row.fullName,
      },
      {
        id: "email",
        header: t("auth.email"),
        defaultVisible: false,
        cell: (row) => row.email,
        exportValue: (row) => row.email,
      },
      {
        id: "roles",
        header: t("users.rolesAndScopes"),
        cell: (row) =>
          row.assignments.length === 0 ? (
            <span className="text-muted-foreground">{t("users.noRoles")}</span>
          ) : (
            <div className="flex flex-wrap gap-1">
              {row.assignments.map((assignment) => (
                <StatusBadge key={assignment.id}>
                  {assignment.roleName}
                  {assignment.scopeType === "TENANT"
                    ? ""
                    : ` · ${assignment.scopeName ?? t(`enums.scopeType.${assignment.scopeType}`)}`}
                </StatusBadge>
              ))}
            </div>
          ),
        exportValue: (row) =>
          row.assignments
            .map((assignment) =>
              assignment.scopeName ? `${assignment.roleName} (${assignment.scopeName})` : assignment.roleName,
            )
            .join(", "),
      },
      {
        id: "mfa",
        header: t("security.mfa.short"),
        cell: (row) => (
          <StatusBadge tone={row.mfaEnabled ? "success" : "neutral"}>
            {row.mfaEnabled ? t("common.on") : t("common.off")}
          </StatusBadge>
        ),
        exportValue: (row) => (row.mfaEnabled ? t("common.on") : t("common.off")),
      },
      {
        id: "lastLoginAt",
        header: t("users.lastLogin"),
        cell: (row) => <span className="tabular">{formatDateTime(row.lastLoginAt)}</span>,
        exportValue: (row) => formatDateTime(row.lastLoginAt),
      },
      {
        id: "status",
        header: t("common.status"),
        cell: (row) => (
          <StatusBadge tone={MEMBERSHIP_TONE[row.membershipStatus]}>
            {t(`enums.membershipStatus.${row.membershipStatus}`)}
          </StatusBadge>
        ),
        exportValue: (row) => t(`enums.membershipStatus.${row.membershipStatus}`),
      },
      ...(canAssign || canManage
        ? [
            {
              id: "actions",
              header: t("common.actions"),
              align: "right" as const,
              hideable: false,
              cell: (row: TenantUser) => (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={t("users.actionsFor", { name: row.fullName })}
                    >
                      <Ellipsis aria-hidden="true" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {canAssign ? (
                      <DropdownMenuItem onSelect={() => setEditing(row)}>
                        {t("users.editRoles")}
                      </DropdownMenuItem>
                    ) : null}
                    {canManage && row.userId !== myId ? (
                      <>
                        <DropdownMenuSeparator />
                        {row.membershipStatus === "ACTIVE" ? (
                          <DropdownMenuItem
                            onSelect={() => setConfirming({ user: row, status: "SUSPENDED" })}
                          >
                            {t("users.suspend")}
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem onSelect={() => setConfirming({ user: row, status: "ACTIVE" })}>
                            {t("users.reactivate")}
                          </DropdownMenuItem>
                        )}
                        {row.membershipStatus !== "REMOVED" ? (
                          <DropdownMenuItem onSelect={() => setConfirming({ user: row, status: "REMOVED" })}>
                            {t("users.remove")}
                          </DropdownMenuItem>
                        ) : null}
                      </>
                    ) : null}
                  </DropdownMenuContent>
                </DropdownMenu>
              ),
            },
          ]
        : []),
    ],
    [t, myId, canAssign, canManage],
  );

  return (
    <>
      <DataTable
        caption={t("users.tabs.users")}
        columns={columns}
        rows={users.data ?? []}
        rowKey={(row) => row.userId}
        loading={users.isPending}
        error={users.error ? <Alert tone="danger">{errorMessage(users.error)}</Alert> : undefined}
        emptyState={
          <EmptyState
            icon={<Users />}
            title={t("users.empty.title")}
            description={t("users.empty.description")}
          />
        }
        onExport={(visible) =>
          exportRowsToExcel({
            fileName: "kullanicilar",
            sheetName: t("menu.users"),
            columns: visible,
            rows: users.data ?? [],
          })
        }
      />
      <Drawer
        open={editing !== null}
        onOpenChange={(open) => !open && setEditing(null)}
        title={t("users.editRoles")}
        {...(editing ? { description: `${editing.fullName} · ${editing.email}` } : {})}
      >
        {editing ? (
          <UserRolesForm
            key={editing.userId}
            user={editing}
            onCancel={() => setEditing(null)}
            onSaved={() => {
              setEditing(null);
              toast({ tone: "success", title: t("common.saved") });
            }}
          />
        ) : null}
      </Drawer>
      {confirming ? (
        <ConfirmDialog
          open
          onOpenChange={(open) => !open && setConfirming(null)}
          title={t(`users.confirm.${confirming.status}.title`)}
          description={t(`users.confirm.${confirming.status}.description`, {
            name: confirming.user.fullName,
          })}
          tone={confirming.status === "ACTIVE" ? "default" : "danger"}
          confirmLabel={t(`users.confirm.${confirming.status}.action`)}
          onConfirm={() =>
            setStatus
              .mutateAsync({ userId: confirming.user.userId, status: confirming.status })
              .then(() => undefined)
          }
        />
      ) : null}
    </>
  );
}

function InvitationsTab(): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const canInvite = usePermissions().has("iam.user.invite");
  const [status, setStatus] = useState<Invitation["status"] | "">("");
  const pages = useCursorPages(status);

  const roles = useQuery({
    queryKey: queryKeys.roles,
    queryFn: async () => (await api.GET("/roles")).data!.items,
  });
  const invitations = useQuery({
    queryKey: [...queryKeys.invitations, { status, cursor: pages.cursor ?? null }],
    queryFn: async () =>
      (
        await api.GET("/invitations", {
          params: {
            query: {
              limit: 25,
              ...(status ? { status } : {}),
              ...(pages.cursor ? { cursor: pages.cursor } : {}),
            },
          },
        })
      ).data!,
    placeholderData: keepPreviousData,
  });
  const revoke = useMutation({
    mutationFn: async (id: string) => api.POST("/invitations/{id}/revoke", { params: { path: { id } } }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.invitations });
      toast({ tone: "success", title: t("users.invitationRevoked") });
    },
    onError: (error) => toast({ tone: "danger", title: errorMessage(error) }),
  });

  const roleNames = useMemo(
    () => new Map((roles.data ?? []).map((role) => [role.id, role.name])),
    [roles.data],
  );
  const columns = useMemo<DataTableColumn<Invitation>[]>(
    () => [
      {
        id: "email",
        header: t("auth.email"),
        hideable: false,
        cell: (row) => (
          <div className="grid">
            <span className="font-medium">{row.email}</span>
            {row.fullName ? <span className="text-xs text-muted-foreground">{row.fullName}</span> : null}
          </div>
        ),
      },
      {
        id: "roles",
        header: t("users.roles"),
        cell: (row) =>
          [...new Set(row.assignments.map((assignment) => roleNames.get(assignment.roleId) ?? "—"))].join(
            ", ",
          ),
      },
      {
        id: "createdAt",
        header: t("users.invitedAt"),
        cell: (row) => <span className="tabular">{formatDateTime(row.createdAt)}</span>,
      },
      {
        id: "expiresAt",
        header: t("users.expiresAt"),
        cell: (row) => <span className="tabular">{formatDateTime(row.expiresAt)}</span>,
      },
      {
        id: "status",
        header: t("common.status"),
        cell: (row) => (
          <StatusBadge tone={INVITATION_TONE[row.status]}>
            {t(`enums.invitationStatus.${row.status}`)}
          </StatusBadge>
        ),
      },
      ...(canInvite
        ? [
            {
              id: "actions",
              header: t("common.actions"),
              align: "right" as const,
              hideable: false,
              cell: (row: Invitation) =>
                row.status === "PENDING" ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={revoke.isPending}
                    onClick={() => revoke.mutate(row.id)}
                  >
                    {t("users.revokeInvitation")}
                  </Button>
                ) : null,
            },
          ]
        : []),
    ],
    [t, roleNames, canInvite, revoke],
  );

  return (
    <DataTable
      caption={t("users.tabs.invitations")}
      columns={columns}
      rows={invitations.data?.items ?? []}
      rowKey={(row) => row.id}
      loading={invitations.isPending}
      error={invitations.error ? <Alert tone="danger">{errorMessage(invitations.error)}</Alert> : undefined}
      emptyState={
        <EmptyState
          icon={<MailPlus />}
          title={t("users.invitationsEmpty.title")}
          description={t("users.invitationsEmpty.description")}
        />
      }
      filters={
        <FormField label={t("common.status")} className="w-44">
          {(control) => (
            <Select
              {...control}
              value={status}
              onChange={(event) => setStatus(event.target.value as Invitation["status"] | "")}
            >
              <option value="">{t("common.all")}</option>
              {INVITATION_STATUSES.map((value) => (
                <option key={value} value={value}>
                  {t(`enums.invitationStatus.${value}`)}
                </option>
              ))}
            </Select>
          )}
        </FormField>
      }
      pagination={{
        hasPrevious: pages.hasPrevious,
        hasNext: invitations.data?.page.hasMore ?? false,
        onPrevious: pages.previous,
        onNext: () => pages.next(invitations.data?.page.nextCursor),
        pageLabel: t("common.pageNumber", { page: pages.pageNumber }),
      }}
    />
  );
}
